<template>
    <form
        @change="isFormDirty"
        @submit.prevent="handleSubmit"
        :class="{ 'form-container': typeForm, 'form-modal-container': typeModal }"
        data-cy="form-container"
    >
        <p class="text-center text-default-red p-2" v-if="validation.message">
            {{ validation.message }}
        </p>
        <div class="form-section mb-8" data-cy="section-general">
            <div class="section-body">
                <template v-for="field in report_params.fields">
                    <!-- text fields -->
                    <div
                        :key="field.id"
                        :data-cy="'field-' + field.field_name"
                        class="input-container w-full px-4 mb-4"
                        v-if="field.field_type == 'text'"
                    >
                        <ui-label>{{ field.label }}</ui-label>
                        <ui-input
                            :class="{ 'form-error': v$.report_data[field.field_name].$error }"
                            v-model="v$.report_data[field.field_name].$model"
                            :placeholder="field.placeholder"
                        />
                        <div
                            data-cy="field-item-error"
                            :class="{ 'text-default-red': v$.report_data[field.field_name].$error }"
                            v-if="
                                field.is_required &&
                                v$.report_data[field.field_name].required?.$invalid &&
                                v$.report_data[field.field_name].$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <validation-errors :errors="validation.errors[field.field_name]" v-if="validation.errors" />
                    </div>

                    <!-- number fields -->
                    <div
                        :key="field.id"
                        :data-cy="'field-' + field.field_name"
                        class="input-container w-full px-4 mb-4"
                        v-if="field.field_type == 'number'"
                    >
                        <ui-label>{{ field.label }}</ui-label>
                        <ui-input-number
                            :class="{ 'form-error': v$.report_data[field.field_name].$error }"
                            v-model="v$.report_data[field.field_name].$model"
                            :placeholder="field.placeholder"
                        />
                        <div
                            data-cy="field-item-error"
                            :class="{ 'text-default-red': v$.report_data[field.field_name].$error }"
                            v-if="
                                field.is_required &&
                                v$.report_data[field.field_name].required?.$invalid &&
                                v$.report_data[field.field_name].$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <validation-errors :errors="validation.errors[field.field_name]" v-if="validation.errors" />
                    </div>

                    <!-- select fields -->
                    <div
                        :key="field.id"
                        :data-cy="'field-' + field.field_name"
                        class="input-container w-full px-4 mb-4"
                        v-if="field.field_type == 'select'"
                    >
                        <ui-label>{{ field.label }}</ui-label>
                        <ui-select
                            :class="{ 'form-error': v$.report_data[field.field_name].$error }"
                            v-model="v$.report_data[field.field_name].$model"
                            :placeholder="field.placeholder"
                            :allow-clear="true"
                            :searchable="true"
                            :options="field.options"
                        />
                        <div
                            data-cy="field-item-error"
                            :class="{ 'text-default-red': v$.report_data[field.field_name].$error }"
                            v-if="
                                field.is_required &&
                                v$.report_data[field.field_name].required?.$invalid &&
                                v$.report_data[field.field_name].$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <validation-errors :errors="validation.errors[field.field_name]" v-if="validation.errors" />
                    </div>

                    <!-- multi-select fields -->
                    <div
                        :key="field.id"
                        :data-cy="'field-' + field.field_name"
                        class="input-container w-full px-4 mb-4"
                        v-if="field.field_type == 'select_multi'"
                    >
                        <ui-label>{{ field.label }}</ui-label>
                        <ui-select-multiple
                            :class="{ 'form-error': v$.report_data[field.field_name].$error }"
                            v-model="v$.report_data[field.field_name].$model"
                            :placeholder="field.placeholder"
                            :searchable="true"
                            :options="selectMultiOptions(field)"
                            :select_mode="'multiple'"
                        />
                        <div
                            data-cy="field-item-error"
                            :class="{ 'text-default-red': v$.report_data[field.field_name].$error }"
                            v-if="
                                field.is_required &&
                                v$.report_data[field.field_name].required?.$invalid &&
                                v$.report_data[field.field_name].$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <validation-errors :errors="validation.errors[field.field_name]" v-if="validation.errors" />
                    </div>

                    <!-- date fields -->
                    <div
                        :key="field.id"
                        :data-cy="'field-' + field.field_name"
                        class="input-container w-1/2 px-4 mb-4"
                        v-if="field.field_type == 'date'"
                    >
                        <ui-label>{{ field.label }}</ui-label>
                        <ui-date-picker
                            :class="{ 'form-error': v$.report_data[field.field_name].$error }"
                            v-model="v$.report_data[field.field_name].$model"
                            :placeholder="field.placeholder"
                        />
                        <div
                            data-cy="field-item-error"
                            :class="{ 'text-default-red': v$.report_data[field.field_name].$error }"
                            v-if="
                                field.is_required &&
                                v$.report_data[field.field_name].required?.$invalid &&
                                v$.report_data[field.field_name].$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <validation-errors :errors="validation.errors[field.field_name]" v-if="validation.errors" />
                    </div>

                    <!-- date range fields -->
                    <div
                        :key="field.id"
                        :data-cy="'field-' + field.field_name"
                        class="input-container w-full px-4 mb-4"
                        v-if="field.field_type == 'date_range'"
                    >
                        <ui-label>{{ field.label }}</ui-label>
                        <ui-date-range-picker-double
                            :class="{ 'form-error': v$.report_data[field.field_name].$error }"
                            v-model="v$.report_data[field.field_name].$model"
                        />
                        <div
                            data-cy="field-item-error"
                            :class="{ 'text-default-red': v$.report_data[field.field_name].$error }"
                            v-if="
                                field.is_required &&
                                v$.report_data[field.field_name].required?.$invalid &&
                                v$.report_data[field.field_name].$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <validation-errors :errors="validation.errors[field.field_name]" v-if="validation.errors" />
                    </div>

                    <!-- time fields -->
                    <div
                        :key="field.id"
                        :data-cy="'field-' + field.field_name"
                        class="input-container w-1/2 px-4 mb-4"
                        v-if="field.field_type == 'time'"
                    >
                        <ui-label>{{ field.label }}</ui-label>
                        <ui-time-picker
                            :class="{ 'form-error': v$.report_data[field.field_name].$error }"
                            v-model="v$.report_data[field.field_name].$model"
                            :placeholder="field.placeholder"
                        />
                        <div
                            data-cy="field-item-error"
                            :class="{ 'text-default-red': v$.report_data[field.field_name].$error }"
                            v-if="
                                field.is_required &&
                                v$.report_data[field.field_name].required?.$invalid &&
                                v$.report_data[field.field_name].$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <validation-errors :errors="validation.errors[field.field_name]" v-if="validation.errors" />
                    </div>

                    <!-- checkbox fields -->
                    <div
                        :key="field.id"
                        :data-cy="'field-' + field.field_name"
                        class="input-container w-full px-4"
                        v-if="field.field_type == 'checkbox'"
                    >
                        <ui-checkbox v-model="report_data[field.field_name]">
                            {{ field.label }}
                        </ui-checkbox>
                    </div>
                    <!-- claims check upload fields and vehicle check upload fields-->
                    <div
                        :key="field.id"
                        :data-cy="'field-' + field.field_name"
                        class="input-container w-full px-4"
                        v-if="field.field_type == 'claim_check_upload'"
                    >
                        <ui-upload-dragger
                            :name="field.field_name"
                            :fileLimit="-1"
                            :upload_text="$t('report.form.upload_text')"
                            :upload_hint="$t('report.form.upload_hint')"
                            v-model="report_data[field.field_name]"
                        />
                    </div>
                    <div
                        :key="field.id"
                        :data-cy="'field-' + field.field_name"
                        class="input-container w-full px-4"
                        v-if="field.field_type == 'vehicle_check_upload'"
                    >
                        <ui-upload-dragger
                            :name="field.field_name"
                            :fileLimit="-1"
                            :upload_text="$t('report.form.upload_text')"
                            :upload_hint="$t('report.form.upload_hint_custom', { file_types: 'TXT, CSV, XSL or XSLX' })"
                            v-model="report_data[field.field_name]"
                            :valid_types="[
                                'text/plain',
                                'text/csv',
                                'application/vnd.ms-excel',
                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                            ]"
                            :valid_types_error="'TXT, CSV, XSL or XSLX'"
                        />
                    </div>
                </template>
            </div>
        </div>
        <div class="form-submit" v-if="form_type == 'form'">
            <ui-submit-button data-cy="submit-button" :disabled="processing">
                <span v-if="!processing">{{ $t('report.form.buttons.save') }}</span>
                <span v-else>{{ $t('report.form.buttons.saving') }}</span>
            </ui-submit-button>
        </div>

        <div class="drawer-footer" v-if="form_type == 'drawer'">
            <a-button data-cy="drawer-cancel-btn" class="btn-cancel" @click="onDrawerClose">
                {{ $t('report.form.buttons.cancel') }}
            </a-button>
            <a-button data-cy="drawer-save-btn" class="ant-btn-primary" :disabled="processing" @click="handleSubmit">
                <span v-if="!processing">{{ $t('report.form.buttons.generate') }}</span>
                <span v-else>{{ $t('report.form.buttons.generating') }}</span>
            </a-button>
        </div>
    </form>
</template>

<script>
import UiCheckbox from '@/components/ui/Checkbox.vue';
import UiDatePicker from '@/components/ui/DatePicker.vue';
import UiDateRangePickerDouble from '@/components/ui/DateRangePickerDouble.vue';
import UiInput from '@/components/ui/Input.vue';
import UiInputNumber from '@/components/ui/InputNumber.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSelect from '@/components/ui/Select.vue';
import UiSelectMultiple from '@/components/ui/SelectMultiple.vue';
import UiSubmitButton from '@/components/ui/SubmitButton.vue';
import UiTimePicker from '@/components/ui/TimePicker.vue';
import UiTextArea from '@/components/ui/TextArea.vue';
import UiUploadDragger from '@/components/ui/UploadDragger.vue';

import ValidationErrors from '@/components/ui/ValidationErrors.vue';

import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    components: {
        UiCheckbox,
        UiDatePicker,
        UiDateRangePickerDouble,
        UiInput,
        UiInputNumber,
        UiLabel,
        UiSelect,
        UiSelectMultiple,
        UiSubmitButton,
        UiTextArea,
        UiTimePicker,
        UiUploadDragger,
        ValidationErrors,
    },
    validations() {
        let validations = {};

        _.forEach(this.report_params.fields, function (value, key) {
            let param = {};

            if (value.is_required == '1') {
                param = Object.assign({}, param, { required });
            } else {
                param = Object.assign({}, param);
            }

            validations[value.field_name] = param;
        });

        return {
            report_data: validations,
        };
    },
    computed: {
        typeForm() {
            return this.form_type !== 'modal';
        },
        typeModal() {
            return this.form_type == 'modal';
        },
    },
    data() {
        let that = this;
        let report_data = {};

        _.forEach(this.report_params.fields, function (value) {
            switch (value.field_type) {
                case 'date':
                    if (value.value !== undefined && value.value !== '' && value.value !== null) {
                        report_data[value.field_name] = that.$dayjs(value.value);
                    } else {
                        report_data[value.field_name] = null;
                    }
                    break;
                case 'time':
                    if (value.value !== undefined && value.value !== '') {
                        report_data[value.field_name] = value.value;
                    } else {
                        report_data[value.field_name] = null;
                    }
                    break;
                case 'date_range':
                    report_data[value.field_name] = {
                        start_date: null,
                        end_date: null,
                    };
                    break;
                case 'checkbox':
                    if (value.value !== undefined && value.value == 'true') {
                        report_data[value.field_name] = true;
                    } else {
                        report_data[value.field_name] = false;
                    }
                    break;
                case 'select_multi':
                    if (value.value !== undefined && value.value !== '' && value.value !== null) {
                        report_data[value.field_name] = _.split(value.value, ',');
                    } else {
                        report_data[value.field_name] = [];
                    }
                    break;
                case 'vehicle_check_upload':
                    report_data[value.field_name] = [];
                    break;
                case 'claim_check_upload':
                    report_data[value.field_name] = [];
                    break;
                default:
                    if (value.value !== undefined) {
                        report_data[value.field_name] = value.value;
                    } else {
                        report_data[value.field_name] = '';
                    }
                    break;
            }
        });

        return {
            report_data: report_data,
        };
    },
    props: {
        mode: String,
        form_type: {
            type: String,
            default: 'drawer',
        },
        report_params: Object,
        processing: {
            type: Boolean,
            default: false,
        },
        validation: Object,
    },
    methods: {
        selectMultiOptions(field) {
            // if field has a dependent or not (example company list is dependent on association list for filtering)
            if (field.dependent_id === undefined) {
                return field.options;
            } else if (
                // if dependent field or list is on the page or not
                this.report_data[field.dependent_id] === undefined ||
                this.report_data[field.dependent_id] === '' ||
                (_.isArray(this.report_data[field.dependent_id]) && this.report_data[field.dependent_id].length == 0)
            ) {
                return field.options;
            } else {
                let dependent_value = this.report_data[field.dependent_id];
                let options = [];

                // filter field list by the dependent current value
                _.forEach(field.options, function (value) {
                    if (
                        (_.isArray(dependent_value) && _.includes(dependent_value, value[field.dependent_id])) ||
                        value[field.dependent_id] == dependent_value
                    ) {
                        options.push(value);
                    }
                });

                let current_values = [];

                // remove field current value if it is no longer in the options list
                _.forEach(this.report_data[field.field_name], function (value) {
                    _.forEach(options, function (options_value) {
                        if (options_value.value == value) {
                            current_values.push(value);
                        }
                    });
                });

                if (JSON.stringify(this.report_data[field.field_name]) !== JSON.stringify(current_values)) {
                    this.report_data[field.field_name] = current_values;
                }

                return options;
            }
        },
        isFormDirty() {
            this.$emit('is-form-dirty', this.v$.$anyDirty);
        },
        async handleSubmit() {
            if (!(await this.v$.$validate())) return false;

            this.$emit('formSubmit', {
                id: this.report_params.id,
                report_data: this.report_data,
            });
        },
        onDrawerClose() {
            this.$emit('onDrawerClose');
        },
    },
};
</script>
